import React, { useEffect, useMemo, useState } from 'react';
import { ConnectionModel } from '../../connection.model';
import { ConnectionItemRenderer } from '../../../../shared/label-renderer/label-renderer';
import CustomSelect from '../../../../shared/custom-select/custom-select';
import CustomButton from '../../../../shared/custom-button/custom-button';
import { PolicySummary } from '../../../policy-summary/policy-summary';
import NoResultIcon from '@svg/no-results.svg?react';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../../../store';
import PolicyInfoHeader from '../policy-info-header/policy-info-header';
import { TooltipDisableReason } from '../../../../shared/tooltip-disable-message/tooltip-disable-reason';
import { ECMActionType, LevelName, VersionLabel} from '../../../../../utils/enums';
import customerService from '../../../../../services/customer.service';
import ValidationError from './validation-error';
import Loader from '../../../../shared/loader/loader';
import { BulkPullPolicyStateItem } from '../../../../../dtos/customer.dto';
import { formatAppliedDate } from '../../../../../utils/datesUtils';
import { EventType } from '../../../../../dtos/analytics.dto';

interface PullFromExistingComponentProps {
    selectedConnections: Map<number, ConnectionModel>;
    setSelectedConnections?: React.Dispatch<React.SetStateAction<Map<number, ConnectionModel>>>;
    showToast: (actionType: ECMActionType, count: number, toApply?: boolean) => void;
}

const INACTIVE_DRAFT_MSG = `Can't create a draft on connection with Inactive policy`;

interface PullAction {
    apply: boolean;
    button: string;
    setLoading: (loading: boolean) => void;
}

const PullFromExistingComponent: React.FC<PullFromExistingComponentProps> = observer(({ selectedConnections, setSelectedConnections, showToast}) => {
    const { customerStore } = useStores();
    const { customerId } = customerStore;
    
    const [selectedActiveVersionNumber, setSelectedActiveVersionNumber] = useState<number | null>(null);
    const [isLoadingApply, setIsLoadingApply] = useState(false);
    const [isLoadingDraft, setIsLoadingDraft] = useState(false);
    const [invalidConnections, setInvalidConnections] = useState<ConnectionModel[] | null>(null);
    const [bulkPullPolicyState, setBulkPullPolicyState] = useState<BulkPullPolicyStateItem[] | null>(null);

    useEffect(() => {
        const fetchActiveVersions = async () => {
            setInvalidConnections(null);
            setBulkPullPolicyState(null);
            try {
                const destinationServiceIdList = Array.from(selectedConnections.keys());
                const result = await customerService.getBulkPullPolicyStateMap(customerId, destinationServiceIdList);
                setBulkPullPolicyState(result || []);
                if (Array.isArray(result) && result.length === 1) {
                    setSelectedActiveVersionNumber(result[0].sid);
                } else {
                    setSelectedActiveVersionNumber(null);
                }
            } catch {
                setBulkPullPolicyState([]);
            }
        };
        void fetchActiveVersions();
    }, [selectedConnections]);

    const isApplyDisabled = bulkPullPolicyState?.length === 0;

    const selectedActiveVersion = useMemo(() =>
            bulkPullPolicyState?.find(conn => conn.sid === selectedActiveVersionNumber)
    , [selectedActiveVersionNumber, bulkPullPolicyState]);

    const isAnySelectedConnectionIsInactive = useMemo(() => {
        return Array.from(selectedConnections.values()).some(conn => conn.policyStatus === VersionLabel.Inactive);
    }, [selectedConnections]);

    const runPull = async ({ apply, button, setLoading }: PullAction) => {
        if (!selectedActiveVersion) return;

        const destinationServiceIdList = Array.from(selectedConnections.keys());
        try {
            setLoading(true);
            await customerStore.bulkPullPolicy(selectedActiveVersion?.sid, destinationServiceIdList, apply);
            customerStore.sendGeneralEvent(
                EventType.LinkClicked,
                {
                    button,
                    number_of_sid: destinationServiceIdList.length,
                    location: 'pullPolicyModal',
                }
            );
            showToast(ECMActionType.PullFromExisting, destinationServiceIdList.length, apply);
            setSelectedConnections?.(new Map());
        } catch {
            showToast(ECMActionType.PullFromExisting, 0, apply);
        }
        finally {
            setLoading(false);
        }
    };

    const pullAndApply = () => runPull({ apply: true, button: 'pullAndApply', setLoading: setIsLoadingApply });
    const pullAsDraft = () => runPull({ apply: false, button: 'pullAsDraft', setLoading: setIsLoadingDraft });

    useEffect(() => {
        const fetchPolicyState = async () => {
            setInvalidConnections(null);
            if (!selectedActiveVersion || selectedConnections.size === 0) {
                return;
            }
            try {
                const destinationServiceIdList = Array.from(selectedConnections.keys());
                const result = await customerService.getAllCustomerServicesDistributePolicyStateByList(
                    customerId,
                    selectedActiveVersion.sid,
                    destinationServiceIdList
                );

                const invalid = result
                    ? Object.values(result)
                        .map(item => (
                            ['INVALID_SERVICE_LEVEL', 'INVALID_EQUAL']).includes(item.distributePolicyState) ? selectedConnections.get(item.targetSid) : null)
                        .filter(Boolean) as ConnectionModel[]
                    : [];

                setInvalidConnections(invalid);
            } catch {
                setInvalidConnections(null);
            }
        };
        void fetchPolicyState();
    }, [selectedActiveVersion]);

    const handleDeselectInvalid = () => {
        if (!setSelectedConnections || !invalidConnections?.length) return;
        const newMap = new Map(selectedConnections);
        invalidConnections.forEach(conn => newMap.delete(conn.id));
        setSelectedConnections(newMap);
    };

    if (!bulkPullPolicyState) {
        return <Loader type="component" />;
    }

    return (
        <div className="pull-from-existing-component">
            <div className="action-type-content">
                {invalidConnections !== null && invalidConnections.length === selectedConnections.size && (
                    <ValidationError message={`The selected source can’t be applied to any of the target connections.\nReselect a source policy.`}  />
                )}
                {invalidConnections !== null && invalidConnections.length > 0 && invalidConnections.length < selectedConnections.size && (
                    <ValidationError
                        message="Can't apply from connection with lower plan or from identical policy"
                        deselectText="Exclude non-compliant connection"
                        deselectedConnections={invalidConnections}
                        onDeselectConnection={handleDeselectInvalid}
                    />
                )}
                {!isApplyDisabled ?
                    <>
                        <CustomSelect placeholder={'Select Connection'}
                                      className={'policy-manager-select-connection'}
                                      onSelect={setSelectedActiveVersionNumber}
                                      value={selectedActiveVersionNumber}
                                      options={bulkPullPolicyState.map(conn => ({
                                          key: conn.sid,
                                          value: conn.sid,
                                          label: conn.serviceName,
                                          data: {
                                              serviceLevel: conn.serviceLevel,
                                              serviceName: conn.serviceName,
                                          }
                                      }))}
                                      emptyMessage={'No connection found'}
                                      LabelRenderer={ConnectionItemRenderer}/>
                        {selectedActiveVersion &&
                            <PolicySummary connectionId={selectedActiveVersion.sid}
                                           externalId={selectedActiveVersion.sidFwMetadata.externalId}
                                           serviceLevel={selectedActiveVersion.serviceLevel as LevelName}
                                           comments={selectedActiveVersion.sidFwMetadata.versionComments}>
                                <PolicyInfoHeader connection={{
                                    policyName:  selectedActiveVersion.sidFwMetadata.versionName,
                                    policyStatus: selectedActiveVersion.sidFwMetadata.versionStatus as VersionLabel,
                                    policyVersionNumber: selectedActiveVersion.sidFwMetadata.versionNumber,
                                    policyApplyDate: new Date(selectedActiveVersion.sidFwMetadata.lastUpdateDateTime),
                                    formattedPolicyApplyDate: formatAppliedDate(selectedActiveVersion.sidFwMetadata.lastUpdateDateTime),
                                }} />
                            </PolicySummary>}
                    </> :
                    <div className="action-type-empty-state">
                        <NoResultIcon />
                        <div className="action-type-empty-state-title">
                            No source connections exist with an applicable policy to apply.
                            Please select one or more connections that are
                            not designated as source connections.
                        </div>
                    </div>
                }
            </div>
            <div className="action-type-footer-btn">
                <TooltipDisableReason overlayClassName="disable-tooltip" disableReason={isAnySelectedConnectionIsInactive ? INACTIVE_DRAFT_MSG : ''}>
                    <CustomButton label="As Draft" className={`secondary-btn action-btn ${isLoadingDraft ? 'loading' : ''}`}
                                  onClick={pullAsDraft}
                                  isDisabled={isAnySelectedConnectionIsInactive || !selectedActiveVersion || isLoadingDraft ||
                                      isLoadingApply || isApplyDisabled || selectedConnections.size === 0 || !invalidConnections || invalidConnections.length > 0}/>
                </TooltipDisableReason>
                <CustomButton label="Apply" className={`primary-btn action-btn ${isLoadingApply ? 'loading' : ''}`}
                              onClick={pullAndApply}
                              isDisabled={isLoadingApply || isLoadingDraft || !selectedActiveVersion ||
                                  isApplyDisabled || selectedConnections.size === 0 || !invalidConnections || invalidConnections.length > 0}
                />
            </div>
        </div>
    );
});

export default PullFromExistingComponent;
